import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { getCroppedImg } from '@/utils/cropImage';

interface CoverImageCropModalProps {
  open: boolean;
  imageSrc: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
}

export const CoverImageCropModal: React.FC<CoverImageCropModalProps> = ({
  open,
  imageSrc,
  onCropComplete,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = (crop: { x: number; y: number }) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropCompleteCallback = useCallback(
    (
      _croppedArea: unknown,
      croppedAreaPixels: { x: number; y: number; width: number; height: number }
    ) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return;

    try {
      setIsProcessing(true);
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImageBlob);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Debug logging
  useEffect(() => {
    if (open) {
      console.log('[CoverImageCropModal] Modal opened with image:', imageSrc.substring(0, 50) + '...');
    }
  }, [open, imageSrc]);

  if (!open || !imageSrc) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 px-8 overflow-hidden bg-white rounded-lg">
        <DialogHeader className="px-0 pt-6 pb-4 border-b bg-white">
          <DialogTitle>Crop Cover Image</DialogTitle>
        </DialogHeader>

        <div className="relative bg-white">
          {/* Crop Area */}
          <div className="relative w-full h-[60vh] bg-gray-900">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              style={{
                containerStyle: {
                  width: '100%',
                  height: '100%',
                },
              }}
            />
          </div>

          {/* Zoom Controls */}
          <div className="px-6 py-4 bg-gray-50 border-t">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 flex-shrink-0">
                <ZoomOut className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700 min-w-[40px]">Zoom</span>
              </div>

              <Slider
                value={[zoom]}
                onValueChange={([value]) => setZoom(value)}
                min={1}
                max={3}
                step={0.1}
                className="flex-1"
              />

              <div className="flex items-center gap-2 flex-shrink-0">
                <ZoomIn className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700 min-w-[40px]">
                  {Math.round(zoom * 100)}%
                </span>
              </div>
            </div>
          </div>

          {/* Cover Aspect Ratio Info */}
          <div className="px-6 pt-4 bg-white border-t">
            <p className="text-xs text-gray-500 text-center">
              Cover aspect ratio: <span className="font-medium">16:9</span>
            </p>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-white gap-2 flex !justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={handleResetCrop}
            disabled={isProcessing}
            className="w-40"
          >
            Reset Crop
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="w-40"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleApplyCrop}
            disabled={isProcessing}
            className="bg-black text-white w-40"
          >
            {isProcessing ? 'Processing...' : 'Apply Crop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
